import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { BreadCrumb } from "primereact/breadcrumb";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { handleGetRequest } from "../../services/GetTemplate";
import { useDispatch } from "react-redux";
import { handlePostRequest } from "../../services/PostTemplate";
import { toast } from "react-toastify";
import axios from "axios";
import { DEV } from "../../services/constants";
import BatchScanner from "../../components/scanning/BatchScanner";
import HandoverGroupModal from "./HandoverGroupModal";
import LowConnectivityCapture from "./LowConnectivityCapture";
import useViewOnly from "../../hooks/useViewOnly";
import styles from "./airportHandover.module.scss";

const STATUS_PILL_CLASS = {
    Scanning: "pillScanning",
    Generated: "pillGenerated",
    Completed: "pillCompleted",
};

const initials = (name) =>
    (name || "")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("") || "?";

const AirportHandoverDetail = () => {
    const { id } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const [handover, setHandover] = useState(null);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scannedPackages, setScannedPackages] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const isAdmin = localStorage.getItem("role") === "admin";
    const viewOnly = useViewOnly();

    const breadItems = [{ label: "Home", url: "/" }, { label: "Airport Handovers", url: "/airport-handovers" }, { label: handover?.handoverId || "..." }];
    const home = { icon: "pi pi-home", url: "/" };

    const load = useCallback(async () => {
        setLoading(true);
        const res = await handleGetRequest(`/airport-handover/${id}`);
        setHandover(res?.data?.handover || null);
        setGroups(res?.data?.groups || []);
        setLoading(false);
    }, [id]);

    // Same fetch, but without toggling the full-page loading state - used
    // for background polling so it doesn't flash the "Loading handover..."
    // screen every few seconds.
    const refreshSilently = useCallback(async () => {
        const res = await handleGetRequest(`/airport-handover/${id}`);
        setHandover(res?.data?.handover || null);
        setGroups(res?.data?.groups || []);
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    // Background OCR resolves a low-connectivity group asynchronously after
    // it's created - poll while any group is still "pending" so the card
    // updates to its final outcome (handed over / needs review / failed)
    // without the staff having to manually reload the page.
    useEffect(() => {
        const hasPending = groups.some((g) => g.processingStatus === "pending");
        if (!hasPending) return;
        const interval = setInterval(refreshSilently, 4000);
        return () => clearInterval(interval);
    }, [groups, refreshSilently]);

    const handleGenerate = async () => {
        if (scannedPackages.length === 0) {
            toast.error("Scan at least one package first");
            return;
        }
        setGenerating(true);
        try {
            const scanRes = await dispatch(
                handlePostRequest({ packageIds: scannedPackages.map((p) => p._id) }, `/airport-handover/${id}/scan`, false, false),
            );
            if (scanRes === "error" || !scanRes?.success) {
                toast.error("Failed to scan packages");
                return;
            }
            if (scanRes.data.rejectedPackageIds?.length) {
                toast.warn(`${scanRes.data.rejectedPackageIds.length} package(s) skipped - already handed over in this session`);
            }

            const generateRes = await dispatch(handlePostRequest({}, `/airport-handover/${id}/generate`, true, true));
            if (generateRes?.success) {
                setScannedPackages([]);
                setIsScanning(false);
                await load();
            } else {
                toast.error("Failed to generate groups");
            }
        } finally {
            setGenerating(false);
        }
    };

    const handleDeleteHandover = () => {
        confirmDialog({
            message: `Delete handover ${handover.handoverId}? This can't be undone. Any packages/clients it already handed over stay as-is - only the handover record itself is removed.`,
            header: "Delete Handover",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                try {
                    const response = await axios.delete(`${DEV}/airport-handover/${id}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    });
                    if (response?.data?.success) {
                        toast.success("Handover deleted");
                        history.push("/airport-handovers");
                    } else {
                        toast.error(response?.data?.message || "Failed to delete handover");
                    }
                } catch (error) {
                    console.error("Error deleting handover:", error);
                    toast.error(error?.response?.data?.message || "Failed to delete handover");
                }
            },
        });
    };

    const filteredGroups = groups.filter((g) => {
        if (!search) return true;
        // Groups the background OCR hasn't resolved yet have no clientName
        // (shown as "Processing..." below) - match against that placeholder
        // too so an empty/irrelevant search doesn't hide in-progress groups.
        return (g.clientName || "Processing...").toLowerCase().includes(search.toLowerCase());
    });

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>Loading handover...</span>
                </div>
            </div>
        );
    }

    if (!handover) {
        return (
            <div className={styles.page}>
                <div className={styles.notFoundState}>
                    <i className="pi pi-exclamation-circle" />
                    <span>Handover not found.</span>
                    <Button label="Back to Handovers" icon="pi pi-arrow-left" outlined onClick={() => history.push("/airport-handovers")} />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <ConfirmDialog />
            <span className={styles.backLink} onClick={() => history.push("/airport-handovers")}>
                <i className="pi pi-arrow-left" />
                Back to Handovers
            </span>
            <div className={styles.pageHeader}>
                <div>
                    <div className={styles.headerTitleRow}>
                        <div className={styles.headerIcon}>
                            <i className={handover.mode === "standard" ? "pi pi-wifi" : "pi pi-camera"} />
                        </div>
                        <div>
                            <h2>{handover.handoverId}</h2>
                            <BreadCrumb model={breadItems} home={home} />
                        </div>
                    </div>
                    <div className={styles.metaBadges}>
                        <span className={`${styles.pill} ${styles.pillMode}`}>
                            <i className={handover.mode === "standard" ? "pi pi-wifi" : "pi pi-camera"} />
                            {handover.mode === "standard" ? "Building" : "Outside Building"}
                        </span>
                        <span className={`${styles.pill} ${styles[STATUS_PILL_CLASS[handover.status]]}`}>{handover.status}</span>
                        {handover.createdBy && (
                            <span className={`${styles.pill} ${styles.pillMode}`}>
                                <i className="pi pi-user" />
                                Created by {handover.createdBy.firstName} {handover.createdBy.lastName}
                            </span>
                        )}
                        {groups.length > 0 && (
                            <span className={`${styles.pill} ${styles.pillMode}`}>
                                <i className="pi pi-sitemap" />
                                {groups.length} group{groups.length === 1 ? "" : "s"}
                            </span>
                        )}
                    </div>
                </div>
                <div className={styles.headerActions}>
                    {handover.status !== "Completed" && handover.mode === "standard" && !isScanning && (
                        <Button
                            label="Scan More Packages"
                            icon="pi pi-qrcode"
                            className={styles.addButton}
                            disabled={viewOnly}
                            tooltip={viewOnly ? "Clock in to scan packages" : undefined}
                            onClick={() => setIsScanning(true)}
                        />
                    )}
                    {isAdmin && <Button label="Delete" icon="pi pi-trash" outlined severity="danger" onClick={handleDeleteHandover} />}
                </div>
            </div>

            {handover.mode === "standard" && (isScanning || groups.length === 0) && (
                <div className={styles.sectionCard}>
                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-qrcode" />
                            Scan Packages
                        </h3>
                    </div>
                    <BatchScanner scannableEndpoint="/airport-handover/scannable-packages" onScannedChange={setScannedPackages} />
                    <div className={styles.generateRow}>
                        <Button
                            label={generating ? "Generating..." : "Generate Groups"}
                            icon={generating ? "pi pi-spin pi-spinner" : "pi pi-check"}
                            className={styles.generateButton}
                            disabled={generating || scannedPackages.length === 0 || viewOnly}
                            onClick={handleGenerate}
                        />
                    </div>
                </div>
            )}

            {handover.mode === "lowConnectivity" && handover.status === "Scanning" && (
                <div className={styles.sectionCard}>
                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-camera" />
                            Capture Labels
                        </h3>
                    </div>
                    {/* One-shot: this handover is either a single-receiver or
                        multiple-receiver batch, submitted once. Once images are
                        sent the backend flips status away from "Scanning" and
                        rejects any further submission, so this form disappears
                        for good on this handover. */}
                    <LowConnectivityCapture handoverId={id} onSubmitted={load} />
                </div>
            )}

            {groups.length > 0 && (
                <div className={styles.sectionCard}>
                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-users" />
                            Client Groups
                        </h3>
                    </div>
                    <div className={styles.searchBar}>
                        <i className="pi pi-search" />
                        <input placeholder="Search by client name..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <div className={styles.groupsGrid}>
                        {filteredGroups.map((group) => {
                            const done = group.status === "HandedOver";
                            const needsReview = group.processingStatus === "needsReview";
                            return (
                                <div
                                    key={group._id}
                                    className={`${styles.groupCard} ${done ? styles.groupCardDone : ""}`}
                                    onClick={() => setSelectedGroup(group)}
                                >
                                    <div className={styles.groupTop}>
                                        <div className={styles.groupIdentity}>
                                            <div className={styles.avatarCircle}>{initials(group.clientName)}</div>
                                            <strong>{group.clientName || "Processing..."}</strong>
                                        </div>
                                        {needsReview ? (
                                            <span className={styles.badgeNeedsReview}>Needs Review</span>
                                        ) : (
                                            <span className={`${styles.badge} ${done ? styles.statusCompleted : styles.statusScanning}`}>{group.status}</span>
                                        )}
                                    </div>
                                    <div className={styles.groupMeta}>
                                        <i className="pi pi-box" />
                                        <span>
                                            {group.packages?.length || 0} package{group.packages?.length === 1 ? "" : "s"}
                                        </span>
                                    </div>
                                    {needsReview ? (
                                        <div className={`${styles.ocrRow} ${styles.ocrRowReview}`}>
                                            <i className="pi pi-exclamation-triangle" />
                                            <span>Photos matched multiple clients - please review</span>
                                        </div>
                                    ) : group.processingStatus === "pending" ? (
                                        <div className={styles.ocrRow}>
                                            <i className="pi pi-spin pi-spinner" />
                                            <span>OCR: processing...</span>
                                        </div>
                                    ) : (
                                        group.processingStatus === "failed" && (
                                            <div className={`${styles.ocrRow} ${styles.ocrRowReview}`}>
                                                <i className="pi pi-times-circle" />
                                                <span>OCR: no package match found</span>
                                            </div>
                                        )
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <HandoverGroupModal
                visible={!!selectedGroup}
                onHide={() => setSelectedGroup(null)}
                handoverId={id}
                group={selectedGroup}
                onSuccess={() => load()}
            />
        </div>
    );
};

export default AirportHandoverDetail;
